import React, {
    useEffect, useContext, useCallback, useState
} from 'react';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import {useDataRefresh, ViewModelForm, ViewModelServiceContext} from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { useModal } from '@jutro/components';
import { FormModalPopover, platformMessages } from 'ml-components-platform-react';
import { UnderlyingPolicy } from 'gw-capability-quoteandbind';
import { LocalDateUtil } from '@xengage/gw-portals-util-js';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { MLLocalDateUtil } from 'ml-portals-util-js';
import { MLNotificationBlockUtil, MLWizardNavUtil } from 'ml-portals-util-react';
import { MLMultiLobsDataHolderService } from 'ml-services-common-react';
import appConfig from 'app-config';
import { UnderlyingPolicyComponent } from 'ml-capability-quoteandbind-common-react';
import { MLTagManagerService } from 'ml-portals-services-react';
import { MLUWIssuesPanelComponent } from 'gw-capability-gateway-quoteandbind-common-react';
import { MLErrorMessageUtil } from 'ml-portals-util-js';
import { MLModalService } from 'ml-portals-services-react';

import { UserService } from 'gw-capability-gateway';
import PUMBTrackEvent from 'gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/PUMBTrackEvent';
import metadata from './UnderlyingPoliciesPage.metadata.json5';
import messages from './UnderlyingPoliciesPage.messages';
import styles from './UnderlyingPoliciesPage.module.scss';

import addEditUnderlyingPolicyPopupMetadata from './AddEditUnderlyingPolicyPopup.metadata.json5';
import confirmRemoveUnderlyingPolicyPopupMetadata from './ConfirmRemoveUnderlyingPolicyPopup.metadata.json5';

import { IntlContext } from '@jutro/legacy/locale';

const UnderlyingPoliciesPage = (props) => {
    const modalApi = useModal();
    const {
        wizardData: submissionVM,
        updateWizardData,
        goNext,
        currentStep,
        updateWizardSnapshot,
        isSkipping,
        steps,
        history
    } = props;

    const {
        showProgressDialog,
        hideProgressDialog,
        showErrorModal
    } = MLModalService();
    const { authHeader } = useAuthentication();
    const translator = useTranslator();
    const intl = useContext(IntlContext);
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const viewModelService = useContext(ViewModelServiceContext);
    const primaryNamedInsured = _.get(submissionVM, 'baseData.primaryNamedInsured_ML.value');
    const [showReturnToQuote, setShowReturnToQuote] = useState(false);
    const quoteStep = steps.find((step) => step.mlStepName === 'quoteDetails');
    const [canRequote, setCanRequote] = useState(false);
    const [isUWIssuesPanelVisible, setIsUWIssuesPanelVisible] = useState(false);
    const [blockingUwIssues, setBlockingUwIssues] = useState([]);
    const [disableNextButton, setDisableNextButton] = useState(false);

    const SECONDARY_NAMED_INSURED = 'secondary_named_insured';
    const OTHER_INSURED = 'other';

    const POLICYTYPE_TYPELIST = viewModelService.productMetadata.get('pc').types.getTypelist('PUPULPolicyType_PUE');
    const CARRIER_TYPELIST = viewModelService.productMetadata.get('pc').types.getTypelist('ULCarrier_PUE');

    const isGpa = appConfig.portalCode === 'GPA';
    const pupModel = _.get(submissionVM, 'lobData.personalUmbrella.value');
    const underlyingPoliciesModel = _.get(submissionVM, 'lobData.value.personalUmbrella.underlyingPolicies');
    const pupModelCoverables = _.get(pupModel, 'coverables');
    const ulPolicies = pupModel.underlyingPolicies;
    const isModernProduct = _.get(submissionVM, "isModernProduct_Ext.value", false);

    const {
        sessionUUID,
        quoteID
    } = submissionVM.value;
    const MLAlertService = MLNotificationBlockUtil();

    const {nextStepVisited, canShowReturnToQuoteButton, isLastStep} = MLWizardNavUtil(props);

    const {
        addSuccessAlert,
        addErrorAlert,
        clearBlockAlerts,
        clearSuccessAlerts
    } = MLNotificationBlockUtil();

    const getFormattedDateString = useCallback((gwDateVal) => {
        const date = LocalDateUtil.toMidnightDate(gwDateVal);
        return intl.formatDate(date, { year: 'numeric', month: '2-digit', day: '2-digit' });
    }, [intl]);

    const getTypeKeyDescription = useCallback((typeKeyName) => {
        return translator({
            id: typeKeyName,
            defaultMessage: typeKeyName
        });
    }, [translator]);

    const setCanRequoteFlag = useCallback ( async() => {
        setCanRequote(showReturnToQuote && _.get(quoteStep, 'visited') !== true);
    }, [quoteStep, showReturnToQuote]);

    useEffect(() => {
        if (isGpa && !isSkipping) {
            UserService.getCurrentUser(authHeader).then((user) => {
                PUMBTrackEvent('Quote Step Reached', isSkipping, submissionVM, user, { step: 'UnderLying Policy Page' });
            });
        }
        canShowReturnToQuoteButton(submissionVM, authHeader, quoteStep).then((response) => {
            setShowReturnToQuote(!!response);
        });
        checkForQuestionUwIssues();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setCanRequoteFlag();
    }, [setCanRequoteFlag, submissionVM]);

    const setDefaultOption = (term) => {
        const { options } = term;

        if(term.name === 'Uninsured Motorist Limit') {
            return ;
        }

        if (options && options.length > 0) {
            if (term.chosenTerm && term.chosenTermValue) {
                const chosenTermAvailableInOptions = options.find((opt) => opt.code === term.chosenTerm);
                if (!chosenTermAvailableInOptions) {
                    _.set(term, 'chosenTerm', options[0].code);
                    _.set(term, 'chosenTermValue', options[0].name);
                    _.set(term, 'updated', true);
                }
            } else {
                _.set(term, 'chosenTerm', options[0].code);
                _.set(term, 'chosenTermValue', options[0].name);
                _.set(term, 'updated', true);
            }
        }
    };

    const checkForQuestionUwIssues = useCallback(() => {
        const uwIssues = submissionVM.errorsAndWarnings?.underwritingIssues?.value;
        if (uwIssues?.length > 0) {
            const questionCheckpointIssues = uwIssues.filter((issue) => {
                return issue.checkingSet_Ext === 'liability_coverage_ml' && issue.currentBlockingPoint === 'BlocksQuote';
            });
            if (questionCheckpointIssues?.length > 0) {
                setIsUWIssuesPanelVisible(true);
                setBlockingUwIssues(questionCheckpointIssues);
                setDisableNextButton(true);
                return true;
            }
            setDisableNextButton(false);
            return false;
        }
        return false;
    }, [submissionVM?.uwIssues_ML?.value]);

    const checkForDisableNextButton = useCallback((issues) => {
        if (issues?.length > 0) {
            const questionCheckpointIssues = issues.filter((issue) => {
                return issue.checkingSet_Ext === 'liability_coverage_ml' && issue.currentBlockingPoint === 'BlocksQuote';
            });
            setDisableNextButton(questionCheckpointIssues?.length > 0);
        }
    }, []);

    const defaultULCoverages = () => {
        ulPolicies.forEach((ulPolicy) => {
            if (!ulPolicy.isExternal) {
                const { coverages } = ulPolicy.ulPolicyCoverage;
                coverages.forEach((cov) => {
                    cov.terms.forEach((term) => {
                        setDefaultOption(term);
                    });
                });
            }
        });
    };

    const getPrimaryNamedInsuredDisplayName = () => {
        return primaryNamedInsured.displayFullName_Ext;
    };

    const getSecondaryNamedInsuredDisplayName = () => {
        const secondaryNamedInsured = submissionVM.baseData.secondaryNamedInsured_ML.value;
        let displayName = '';
        if (!_.isNil(secondaryNamedInsured)) {
            displayName = `${secondaryNamedInsured.firstName} ${secondaryNamedInsured.middleName} ${secondaryNamedInsured.lastName}`;
        }

        return displayName;
    };

    const getInsuredDisplayName = (item) => {
        let displayName = '';
        if (item.namedInsured === OTHER_INSURED) {
            displayName = item.otherInsured;
        } else if (item.namedInsured === SECONDARY_NAMED_INSURED) {
            displayName = getSecondaryNamedInsuredDisplayName();
        } else {
            displayName = getPrimaryNamedInsuredDisplayName();
        }
        return displayName;
    };

    const setInsuredDisplayNameForPolicy = (ulPolicy) => {
        _.set(ulPolicy, 'insuredDisplayName', getInsuredDisplayName(ulPolicy));
    };

    const setInsuredDisplayNames = () => {
        ulPolicies.forEach((ulPolicy) => {
            setInsuredDisplayNameForPolicy(ulPolicy);
        });
    };
    
    const getUnderlyingPolicyVM = useCallback((underlyingPolicy) => {
        return submissionVM.lobData.personalUmbrella.underlyingPolicies.findFirstElement(underlyingPolicy);
    }, [submissionVM]);

    const setErrorStatusMessage = useCallback(() => {
        const alertMsg = (
            <FormattedMessage
                {...messages.ulPolicyErrorAlertMessage}
                values={
                    {
                        policiesNeedAttention: (
                            <strong>{translator(messages.policiesMarkedNeedAttention)}</strong>
                        )
                    }
                }
            />
        );
        addErrorAlert(alertMsg, 'underlyingPolicyError');
    }, [translator, addErrorAlert]);

    const isExpirationDateEarlier = (ulPolicy) => {
        if (ulPolicy.effectiveDate && ulPolicy.expirationDate) {
            const isEarlier = MLLocalDateUtil.compare(ulPolicy.effectiveDate, ulPolicy.expirationDate);
            if (isEarlier > 0) {
                return true;
            }
        }
        return false;
    };

    const checkAllPolicyForError = useCallback(() => {
        const policyErrors = ulPolicies.some((policy) => policy.error);
        clearBlockAlerts('underlyingPolicyError');
        if (policyErrors) {
            setErrorStatusMessage();
        }
    }, [ulPolicies, setErrorStatusMessage, clearBlockAlerts]);

    const hasNoChosenTerms = (terms) => {
        let noChosenTerms = false;
        terms.forEach((term) => {
            if (noChosenTerms) {
                return;
            }
            noChosenTerms = _.isEmpty(term.chosenTerm) && _.isEmpty(term.directValue) && _.isEmpty(term.genericValue_ML);
        });

        return noChosenTerms;
    };

    const coveragesHasChosenTerms = useCallback((coverages) => {
        let valid = true;
        coverages.forEach((coverage) => {
            if (hasNoChosenTerms(coverage.terms)) {
                valid = false;
            }
        });
        return valid;
    }, []);

    const validateUnderlyingPolicy = useCallback((ulPolicy) => {
        if (!coveragesHasChosenTerms(_.get(ulPolicy,'ulPolicyCoverage.coverages',[])) || isExpirationDateEarlier(ulPolicy)) {
            _.set(ulPolicy, 'error', true);
            return true;
        }
        _.set(ulPolicy, 'error', false);
        return false;
    }, [coveragesHasChosenTerms]);

    const validatePrefilledPolicies = useCallback(() => {
        ulPolicies.forEach((ulPolicy) => {
            validateUnderlyingPolicy(ulPolicy);
        });
        checkAllPolicyForError();
    }, [checkAllPolicyForError, ulPolicies, validateUnderlyingPolicy]);

    const getAlertMessage = useCallback((policy) => {
        let carrier = null;
        if (policy.carrier) {
            carrier = policy.carrier === 'Other' ? policy.otherCarrier : getTypeKeyDescription(CARRIER_TYPELIST.getCode(policy.carrier).name);
        }

        return {
            policyType: policy.policyType ? getTypeKeyDescription(POLICYTYPE_TYPELIST.getCode(policy.policyType).name) : null,
            carrier
        };
    }, [CARRIER_TYPELIST, POLICYTYPE_TYPELIST, getTypeKeyDescription]);

    const addAlert = (policy) => {
        const msgArgs = getAlertMessage(policy);
        const alertMsg = (
            <FormattedMessage
                {...messages.addPolicyAlertMessage}
                values={
                    {
                        policyType: (
                            <strong>{!(isModernProduct && msgArgs.policyType === "Homeowners") ? translator(msgArgs.policyType): translator("Property")}</strong>
                        ),
                        carrier: (
                            <strong>{translator(msgArgs.carrier)}</strong>
                        )
                    }
                }
            />
        );
        const alertName = `Added${JSON.stringify(msgArgs)}`;
        addSuccessAlert(alertMsg, alertName);
    };

    const handleSavingUnderlyingPolicy = (ulPolicyVM) => {
        MLTagManagerService.sendFormStepEvent(MLTagManagerService.getFormName(), `${MLTagManagerService.getStepName()}_Add Policy`);
        validateUnderlyingPolicy(ulPolicyVM.value);
        setInsuredDisplayNameForPolicy(ulPolicyVM.value);
        clearSuccessAlerts();
        addAlert(ulPolicyVM.value);
        window.scrollTo(0, 0);
        setDisableNextButton(false);
    };

    const cancelAddUnderlyingPolicy = (policy) => {
        pupModel.removeUnderlyingPolicy(policy);
        updateWizardData(submissionVM);
    };

    const onAddPolicy = () => {
        MLTagManagerService.sendLinkClickEvent(`${MLTagManagerService.getStepName()}_Add Policy`);
        const newUnderlyingPolicy = pupModel.createUnderlyingPolicy();
        const underlyingVM = submissionVM.lobData.personalUmbrella.underlyingPolicies.findFirstElement(newUnderlyingPolicy);
        const componentProps = {
            title: messages.addAPolicy,
            viewModel: underlyingVM,
            uiProps: addEditUnderlyingPolicyPopupMetadata.pageContent,
            actionBtnLabel: isGpa ? messages.addPolicy : platformMessages.mlContinue,
            cancelBtnLabel: isGpa ? platformMessages.mlCancel : undefined,
            onSaveAction: (underlyingPolicyVM) => {
                handleSavingUnderlyingPolicy(underlyingPolicyVM);
                checkAllPolicyForError();
                updateWizardData(submissionVM);

                return true;
            },
            getOverrideProps: () => {
                return {
                    underlyingPolicyComponent: {
                        underlyingPolicyVM: underlyingVM,
                        sessionUUID,
                        quoteID,
                        isEdit: false,
                        authHeader,
                        isComponent: true,
                        MLAlertService,
                        isModernProduct
                    }
                };
            },
            customComponentResolvers: {
                underlyingPolicyComponent: UnderlyingPolicyComponent
            }
        };

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        ).catch(() => cancelAddUnderlyingPolicy(underlyingVM.value));
    };

    const createCopyUnderlyingPolicy = (policy) => {
        const duplicatePolicy = new UnderlyingPolicy(policy);
        const {
            _xCenter,
            _dtoName
        } = submissionVM.lobData.personalUmbrella.underlyingPolicies;
        return viewModelService.create(duplicatePolicy, _xCenter, _dtoName);
    };

    const handleSavePolicyEdit = (ulPolicyVM, ulPolicyVMtoEdit) => {
        validateUnderlyingPolicy(ulPolicyVM.value);
        setInsuredDisplayNameForPolicy(ulPolicyVM.value);
        _.set(ulPolicyVMtoEdit, 'value', ulPolicyVM.value);
        MLTagManagerService.sendFormStepEvent(MLTagManagerService.getFormName(), `${MLTagManagerService.getStepName()}_Edit Policy`);
        setDisableNextButton(false);
    };

    const onEditPolicy = (item) => {
        MLTagManagerService.sendLinkClickEvent(`${MLTagManagerService.getStepName()}_Edit Policy`);

        const policy = ulPolicies[item.index];
        const policyToBeEdited = getUnderlyingPolicyVM(policy);
        const copyOfUnderlylingPolicyVm = createCopyUnderlyingPolicy(policy);

        const componentProps = {
            title: messages.editPolicy,
            viewModel: copyOfUnderlylingPolicyVm,
            uiProps: addEditUnderlyingPolicyPopupMetadata.pageContent,
            actionBtnLabel: isGpa ? messages.editPolicy : platformMessages.mlContinue,
            cancelBtnLabel: isGpa ? platformMessages.mlCancel : undefined,
            onSaveAction: (underlyingPolicyVM) => {
                handleSavePolicyEdit(underlyingPolicyVM, policyToBeEdited);
                checkAllPolicyForError();
                updateWizardData(submissionVM);

                return true;
            },
            getOverrideProps: () => {
                return {
                    underlyingPolicyComponent: {
                        underlyingPolicyVM: copyOfUnderlylingPolicyVm,
                        sessionUUID,
                        quoteID,
                        isEdit: true,
                        authHeader,
                        isComponent: true,
                        MLAlertService,
                        isModernProduct
                    }
                };
            },
            customComponentResolvers: {
                underlyingPolicyComponent: UnderlyingPolicyComponent
            }
        };

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        );
    };

    const deleteExposuresForULPolicy = (model, toRemove) => {
        return model.filter((el) => {
            return toRemove.indexOf(el) < 0;
        });
    };

    const saveDeletedExposures = (item) => {
        const locationExposuresToDelete = pupModelCoverables.locations.filter((exp) => exp.ulPolicyFixID === item.ulPolicyFixID);
        const vehicleExposuresToDelete = pupModelCoverables.vehicles.filter((exp) => item.ulPolicyFixID === exp.ulPolicyFixID);
        const watercraftOperatorExposuresToDelete = pupModelCoverables.watercraftOperators.filter((exp) => item.ulPolicyFixID === exp.ulPolicyFixID);
        const watercraftExposuresToDelete = pupModelCoverables.watercrafts.filter((exp) => item.ulPolicyFixID === exp.ulPolicyFixID);

        const deletedExposures = {
            locations: locationExposuresToDelete,
            vehicles: vehicleExposuresToDelete,
            watercraftOperators: watercraftOperatorExposuresToDelete,
            watercrafts: watercraftExposuresToDelete
        };

        pupModelCoverables.locations = locationExposuresToDelete.length > 0 ? deleteExposuresForULPolicy(pupModelCoverables.locations, locationExposuresToDelete) : pupModelCoverables.locations;
        pupModelCoverables.vehicles = vehicleExposuresToDelete.length > 0 ? deleteExposuresForULPolicy(pupModelCoverables.vehicles, vehicleExposuresToDelete) : pupModelCoverables.vehicles;
        pupModelCoverables.watercraftOperators = watercraftOperatorExposuresToDelete.length > 0 ? deleteExposuresForULPolicy(pupModelCoverables.watercraftOperators, watercraftOperatorExposuresToDelete) : pupModelCoverables.watercraftOperators;
        pupModelCoverables.watercrafts = watercraftExposuresToDelete.length > 0 ? deleteExposuresForULPolicy(pupModelCoverables.watercrafts, watercraftExposuresToDelete) : pupModelCoverables.watercrafts;

        return deletedExposures;
    };

    const undoDeletedExposures = useCallback((deletedExposures) => {
        deletedExposures.locations.forEach((loc) => pupModelCoverables.locations.push(loc));
        deletedExposures.vehicles.forEach((veh) => pupModelCoverables.vehicles.push(veh));
        deletedExposures.watercraftOperators.forEach((wat) => pupModelCoverables.watercrafts.push(wat));
        deletedExposures.watercraftOperators.forEach((op) => pupModelCoverables.watercraftOperators.push(op));
    }, [pupModelCoverables]);

    const undoPolicyRemoval = useCallback((policy, deletedExposures, alertName) => {
        if (!policy) {
            return;
        }
        pupModel.underlyingPolicies.push(policy);
        undoDeletedExposures(deletedExposures);
        checkAllPolicyForError();

        if (alertName) {
            clearBlockAlerts(alertName);
        }
    }, [checkAllPolicyForError, clearBlockAlerts, pupModel.underlyingPolicies, undoDeletedExposures]);

    const addRemovalAlert = useCallback((policy, deletedExposures) => {
        clearSuccessAlerts();
        const msgArgs = getAlertMessage(policy);
        const alertMsg = {...messages.removePolicyAlertMessage, args: {
            policyType: translator(msgArgs.policyType),
            carrier: translator(msgArgs.carrier)
        }};
        const alertName = `Removed${JSON.stringify(msgArgs)}`;
        addSuccessAlert(alertMsg, alertName, () => undoPolicyRemoval(policy, deletedExposures, alertName));
    }, [clearSuccessAlerts, getAlertMessage, translator, addSuccessAlert, undoPolicyRemoval]);

    const removePolicy = (policy) => {
        const deletedExposures = saveDeletedExposures(policy);
        pupModel.removeUnderlyingPolicy(policy);
        addRemovalAlert(policy, deletedExposures);
        checkAllPolicyForError();
        updateWizardData(submissionVM);
        MLTagManagerService.sendFormStepEvent(MLTagManagerService.getFormName(), `${MLTagManagerService.getStepName()}_Remove Policy`);
        setDisableNextButton(false);
    };

    const onRemovePolicy = (item) => {
        MLTagManagerService.sendLinkClickEvent(`${MLTagManagerService.getStepName()}_Remove Policy`);
        const policy = ulPolicies[item.index];
        const componentProps = {
            title: messages.removePolicy,
            uiProps: confirmRemoveUnderlyingPolicyPopupMetadata.pageContent,
            actionBtnLabel: messages.remove,
            cancelBtnLabel: messages.cancel,
            showXCloseButton: false,
            onSaveAction: () => {
                removePolicy(policy);
                return true;
            }
        };
        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        );
    };
    
    const hasUnderlyingAutoPolicy = useCallback(() => {
        if(isModernProduct) {
          if (ulPolicies.filter((ulPolicy) => ulPolicy.policyType === 'PA').length === 0) {
            addErrorAlert(messages.noAutoUlPolicyErrorAlertMessage, 'underlyingPolicyError');
            return false;
           }
        }
        return true;
    }, [addErrorAlert, ulPolicies]);

    const areUnderlyingPoliciesValid = useCallback(() => {
        return ulPolicies.filter((ulPolicy) => ulPolicy.error).length === 0;
    }, [ulPolicies]);

    const nextStepText_Ext = () => {
        return (
            <FormattedMessage
                {...messages.nextDescription}
                values={{
                    reviewVehiclesDrivers: (
                        <strong>{translator(messages.reviewVehiclesDrivers)}</strong>
                    )
                }}
            />
        );
    };

    const validateData = useCallback( async (isReturnToQuote= false) => {
        _.set(submissionVM, 'portalCurrentStep_ML.value', currentStep.mlStepName);

        validatePrefilledPolicies();
        const hasAutoUlPol = hasUnderlyingAutoPolicy();
        const isValid = areUnderlyingPoliciesValid();

        if (isReturnToQuote && isValid && hasAutoUlPol) {
            MLMultiLobsDataHolderService.storeFlowSteps(currentStep.mlStepName, 'quoteDetails', authHeader);
        }
        
        return isValid && hasAutoUlPol;
    }, [areUnderlyingPoliciesValid, authHeader, currentStep.mlStepName, hasUnderlyingAutoPolicy, submissionVM, validatePrefilledPolicies]);

    const onNext = useCallback( async () => {
        if (isGpa) {
            UserService.getCurrentUser(authHeader).then((user) => {
                PUMBTrackEvent('Quote Step Submitted', false, submissionVM, user, { step: 'UnderLying Policy Page', action: 'Underlying Policy Submitted'});
            });
        }

        const isDataValid = await validateData();
        if (!isDataValid) {
            return false;
        }
        
        updateWizardSnapshot(submissionVM);
        MLMultiLobsDataHolderService.storeFlowSteps(currentStep.mlStepName, 'pupVehicleDriverHouseHoldDetails', authHeader);

        if (history?.location?.state?.returnToQuote) {
            history.location.state.returnToQuote = undefined;
        }

        try {
            showProgressDialog(platformMessages.saveSubmission);

            const canSkipSave = steps.some((step) => step.mlStepName === 'pupVehicleDriverHouseHoldDetails' && !!step.visited);
            if (!canSkipSave) {
                setIsUWIssuesPanelVisible(false);
                const response = await LoadSaveService.updateDraftSubmission(submissionVM.value, authHeader);
                submissionVM.value = response;
                updateWizardData(submissionVM);
                MLMultiLobsDataHolderService.storeFlowSteps(currentStep.mlStepName, 'pupVehicleDriverHouseHoldDetails', authHeader);
            }
            if (checkForQuestionUwIssues()) {
                return false;
            }
        } catch (error) {
            MLErrorMessageUtil.handleException(error, showErrorModal, messages.somethingWentWrong);
        } finally {
            hideProgressDialog();
        }

        if (!nextStepVisited()) {
            return submissionVM;
        }
    
        goNext();
    }, [isGpa, validateData, updateWizardSnapshot, submissionVM, currentStep.mlStepName, authHeader, nextStepVisited, history.location.state, goNext, MLErrorMessageUtil, hideProgressDialog, LoadSaveService]);

    useEffect(() => {
        if (ulPolicies.length > 0) {
            defaultULCoverages();
            validatePrefilledPolicies();
            setInsuredDisplayNames();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const generateOverrides = () => {
        const overrides = ulPolicies.map((policy, index) => {
            let carrier = null;
            if (policy.carrier) {
                carrier = getTypeKeyDescription(CARRIER_TYPELIST.getCode(policy.carrier).name);
                if (carrier === 'Other') {
                    carrier = policy.otherCarrier;
                }
            }

            return {
                [`policyTypeCell${index}`]: {
                    className: `cell ${_.get(policy, 'error', false) ? 'error-help-text font-weight-bold' : ''}`
                },
                [`policyTypeErrorAlertIcon${index}`]: {
                    visible: _.get(policy, 'error', false)
                },
                [`policyTypeCellMobileHeader${index}`]: {
                    content: `${_.upperCase(translator(messages.policyType))}:`
                },
                [`policyCarrierCellMobileHeader${index}`]: {
                    content: `${_.upperCase(translator(messages.carrier))}:`
                },
                [`policyEffectiveDateCellMobileHeader${index}`]: {
                    content: `${_.upperCase(translator(messages.effectiveDate))}:`
                },
                [`policyExpirationDateCellMobileHeader${index}`]: {
                    content: `${_.upperCase(translator(messages.expirationDate))}:`
                },
                [`policyInsuredCellMobileHeader${index}`]: {
                    content: `${_.upperCase(translator(messages.insured))}:`
                },
                [`policyType${index}`]: {
                    content: policy.policyType ? !(isModernProduct && policy.policyType === 'HO') ? getTypeKeyDescription(POLICYTYPE_TYPELIST.getCode(policy.policyType).name) : translator({id: 'property', defaultMessage: "Property"}) : null
                },
                [`policyCarrier${index}`]: {
                    content: carrier
                },
                [`policyEffectiveDate${index}`]: {
                    content: policy.effectiveDate ? getFormattedDateString(policy.effectiveDate) : null
                },
                [`policyExpirationDate${index}`]: {
                    content: policy.expirationDate ? getFormattedDateString(policy.expirationDate) : null
                },
                [`policyInsured${index}`]: {
                    content: policy.insuredDisplayName ? _.startCase(_.toLower(policy.insuredDisplayName)) : null
                },
                [`editPolicy${index}`]: {
                    'aria-labelledby': `policyType${index} policyCarrier${index} policyEffectiveDate${index} policyExpirationDate${index} policyInsured${index}`
                },
                [`removePolicy${index}`]: {
                    'aria-labelledby': `policyType${index} policyCarrier${index} policyEffectiveDate${index} policyExpirationDate${index} policyInsured${index}`
                }
            };
        });

        return Object.assign({}, ...overrides);
    };

    const overrideProps = {
        gpaPageHeader: {
            visible: isGpa
        },
        underlyingPoliciesContainer: {
            className: isGpa ? 'page-content-gpa' : undefined
        },
        underlyingPoliciesHeader: {
            content: translator(isGpa ? messages.policies : messages.yourPolicies),
            className: isGpa ? undefined : 'page-header vertical-spacing-10'
        },
        underlyingPoliciesDescription: {
            content: translator(ulPolicies.length > 0 ? messages.foundPoliciesToBeIncluded : messages.noPoliciesIncluded)
        },
        policiesIterable: {
            data: ulPolicies
        },
        nextButton: {
            disabled: !areUnderlyingPoliciesValid()
        },
        nextDescription: {
            content: nextStepText_Ext()
        },
        returnToQuoteButton: {
            visible: showReturnToQuote,
            disabled: true
        },
        uwIssueContainer: {
            visible: isUWIssuesPanelVisible,
            uwIssues: blockingUwIssues,
            jobID: submissionVM.quoteID.value,
            publicID: _.get(submissionVM, 'value.publicID'),
            afterIssuesRefreshedCallback: checkForDisableNextButton,
            submissionVM
        },
        ...generateOverrides()
    };

    const resolvers = {
        resolveCallbackMap: {
            onEditPolicy,
            onAddPolicy,
            onRemovePolicy,
            onNext
        },
        resolveClassNameMap: styles,
        resolveComponentMap: {
            mluwissuespanelcomponent: MLUWIssuesPanelComponent
        }
    };

    const skipOnResumeIf = () => {
        if (!!_.get(submissionVM, 'bindData.policyNumber.value')
            && (submissionVM.value.baseData.status_ML === 'Bound' || submissionVM.value.baseData.status_ML === 'Binding')) {
            return true;
        }

        if (MLMultiLobsDataHolderService.getLastVisitedStep() === currentStep.mlStepName) {
            return false;
        }

        return true;
    };

    return (
        <WizardPage
            skipWhen={skipOnResumeIf}
            showCancel={false}
            showPrevious={false}
            showNext={!nextStepVisited()}
            disableNext={disableNextButton}
            onNext={onNext}
            nextLabel={translator(platformMessages.mlContinue)}
            showTextNextToButton={!nextStepVisited()}
            textNextToButton={nextStepText_Ext()}
            metadataPageId={!nextStepVisited() && _.get(metadata, 'pageContent.id')}
            showReturnToQuote={showReturnToQuote && canRequote}
            disableReturnToQuote={!canRequote || disableNextButton}
            updateWizardData={updateWizardData}
            validateData={validateData}
            history={history}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
};

UnderlyingPoliciesPage.propTypes = wizardProps;
export default withRouter(UnderlyingPoliciesPage);